import React, { useCallback, useMemo } from 'react';
import cn from 'classnames';

import IMG_CLOCK_ICON from 'assets/images/icons/clock-purple.svg';
import determineHoursLaterForNcob from 'bl/ncob/determineHoursLaterForNcob.bl';
import DropdownInput from 'components/form/DropdownInput.component';
import KnobInput from 'components/form/KnobInput.component';
import TIMING_BEFORE from 'constants/salon/timingBefore.const';
import NCOB_HOURS from 'constants/settings/ncobHours.const';
import NCOB_OFFSET_HOURS from 'constants/settings/ncobOffsetHours.const';
import useResettingState from 'hooks/useResettingState.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import asObject from 'utils/objects/asObject.util';
import isMissing from 'utils/predicates/any/isMissing.util';
import isEnumVal from 'utils/predicates/enum/isEnumVal.util';
import createNodeKey from 'utils/react/createNodeKey.util';

import CN from './WidgetTiming.module.scss';

const HOURS_BOTTOM_OPTION = Object.freeze({
  id: 'sameDay',
  descriptionTranslateKey: 'composite.widgetTiming.sameDay',
  hour: '02:00:00',
  int: 2,
  before: TIMING_BEFORE.sameDay,
});
const HOURS_TOP_ROW = Object.freeze(Object.values(NCOB_HOURS));
const HOURS_PRESET_KEYS = Object.freeze(Object.keys(NCOB_HOURS));
const DEFAULT_HOURS_OFFSET = '02:00:00';

const byValue = ($) => (candidate) => asObject(candidate).value === $;
const byDefaultHours = byValue(DEFAULT_HOURS_OFFSET);

const determineOption = (value) => {
  if (isEnumVal(NCOB_HOURS, value)) return value;

  const found = NCOB_HOURS[fromAnyToTrimmed(value)];
  if (found) return found;

  return HOURS_BOTTOM_OPTION;
};

const WidgetTiming = ({ name: nameFromProps, tag: tagFromProps, value: valueFromProps, onChangeTiming = alwaysNoop, onChangeHours = alwaysNoop, offsetHours = NCOB_OFFSET_HOURS }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const name = fromAnyToTrimmed(nameFromProps);
  const tag = fromAnyToTrimmed(tagFromProps);

  // NOTE: the resetting can be changed with the second parameter being useResettingState.NIL or useResettingState.NULL
  const [selected, setSelected] = useResettingState(determineOption(valueFromProps));
  const selectedId = asObject(selected).id;

  const handleClick = useCallback(
    //
    async (ev) => {
      const { raw } = asObject(ev);
      const { value } = asObject(await call(`WidgetTiming.onChangeTiming`, onChangeTiming, { name, tag, raw }));

      setSelected(isMissing(value) ? raw : determineOption(value));
    },
    [name, onChangeTiming, setSelected, tag],
  );

  const hoursValue = useMemo(() => determineHoursLaterForNcob(valueFromProps), [valueFromProps]);
  const hoursUnit = useMemo(() => (hoursValue === 1 ? t('common.hours.hour') : t('common.hours.hours')), [hoursValue, t]);

  const isDropdownDisabled = HOURS_PRESET_KEYS.includes(valueFromProps);
  const isDropdownVisible = !HOURS_PRESET_KEYS.includes(valueFromProps);

  const hoursObject = isDropdownVisible ? offsetHours.find(byValue(valueFromProps)) || offsetHours.find(byDefaultHours) : null;

  return (
    <div
      data-bem="WidgetTiming"
      data-tag={tag}
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div data-bem="WidgetTiming__list" data-tag={tag} className={CN.list}>
        {HOURS_TOP_ROW.map((option) => (
          <KnobInput tag="WidgetTiming__item" key={createNodeKey({ unique: option.id })} name={option.id} value={option} selected={selectedId === option.id} onClick={handleClick}>
            {option.description}
          </KnobInput>
        ))}
      </div>
      <div data-bem="WidgetTiming__bottom" data-tag={tag} className={CN.list}>
        <KnobInput
          tag="WidgetTiming__item"
          key={createNodeKey({ unique: HOURS_BOTTOM_OPTION.id })}
          name={HOURS_BOTTOM_OPTION.id}
          value={HOURS_BOTTOM_OPTION}
          selected={selectedId === HOURS_BOTTOM_OPTION.id}
          onClick={handleClick}
        >
          {t(HOURS_BOTTOM_OPTION.descriptionTranslateKey)}
        </KnobInput>
        {isDropdownVisible && (
          <div className={CN.hoursButton}>
            <div
              className={cn({
                [CN.hoursOffset]: true,
                [CN.disabled]: isDropdownDisabled,
              })}
            >
              <DropdownInput align="top" disabled={isDropdownDisabled} onChange={onChangeHours} options={offsetHours} value={hoursObject} />
            </div>
            <div className={CN.dropdownText}>
              <img className={CN.icon} src={IMG_CLOCK_ICON} alt="" />
              {hoursUnit} {t('composite.widgetTiming.before')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default WidgetTiming;
